import { useCubeQuery } from '@cubejs-client/react';
import { LabelOutlined } from '@mui/icons-material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CommoditySourceSystemField } from '~/common/types/cubes/CommoditySourceSystem';
import { DimCommodityField } from '~/common/types/cubes/DimCommodity';
import { BaseDetailHeader, BaseDetailHeaderProps } from '~/components/BaseDetailHeader/BaseDetailHeader';

import { Description } from './CommodityDetailHeader.styled';

export interface CommodityDetailHeaderProps {
  id: string;
  tabs?: BaseDetailHeaderProps['tabs'];
  actionsRight?: ReactNode;
}

export const CommodityDetailHeader = ({ id, tabs, actionsRight }: CommodityDetailHeaderProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'CommodityDetail' });

  const descriptionQuery = useCubeQuery({
    dimensions: [DimCommodityField.DESCRIPTION],
    filters: [{ member: DimCommodityField.ID, operator: 'equals', values: [id] }],
  });
  const sourceSystemQuery = useCubeQuery({
    dimensions: [
      CommoditySourceSystemField.SOURCE_REFERENCE,
      CommoditySourceSystemField.LINK,
      CommoditySourceSystemField.SOURCE_SYSTEM,
    ],
    filters: [{ member: CommoditySourceSystemField.INTERNAL_ID, operator: 'equals', values: [id] }],
  });

  const loading = descriptionQuery.isLoading || sourceSystemQuery.isLoading;

  const description = (descriptionQuery.resultSet?.rawData()?.[0]?.[DimCommodityField.DESCRIPTION] as string) ?? '';

  const sourceSystemRows = sourceSystemQuery.resultSet?.rawData() ?? [];
  const externalId = (sourceSystemRows[0]?.[CommoditySourceSystemField.SOURCE_REFERENCE] as string) ?? '';
  const links = sourceSystemRows.map((row) => ({
    title: `${row[CommoditySourceSystemField.SOURCE_REFERENCE] ?? ''}`,
    url: `${row[CommoditySourceSystemField.LINK] ?? ''}`,
  }));

  return (
    <BaseDetailHeader
      loading={loading}
      title={t('title', { id: externalId })}
      tabs={tabs}
      links={links}
      actionsRight={actionsRight}
      center={
        <Description variant="body2" color="textPrimary" startIcon={<LabelOutlined fontSize="small" />}>
          {description}
        </Description>
      }
    />
  );
};
